import { forwardRef, Injectable } from '@angular/core';
import { FirebaseOptions } from 'firebase/app';
import { DesktopFirebaseService } from './desktop.firebase.service';
import { MobileFirebaseService } from './mobile.firebase.service';
import { User } from '@capacitor-firebase/authentication';
import { UserInfo } from 'firebase/auth';

@Injectable({
  providedIn: 'root',
  useClass: forwardRef(() => DesktopFirebaseService),
})
export abstract class FirebaseService {
  abstract signInWithEmailAndPassword(email: string, password: string): Promise<string | undefined>;
  abstract signInWithProvider(provider: string): Promise<string | undefined>;
  abstract signUp(email: string, password: string): Promise<string | undefined>;
  abstract signOut(): Promise<void>;
  abstract sendPasswordResetEmail(email: string): Promise<boolean>;
  abstract getIdToken(forceRefresh?: boolean): Promise<string | undefined>;
  abstract signUpProvider(
    provider: string,
  ): Promise<{ token?: string; user?: Pick<UserInfo, 'email' | 'displayName' | 'photoURL'> }>;
  abstract getUser(): Promise<User | null>;

  /**
   * The signed-in user, waiting up to `timeoutMs` for Firebase to restore it.
   *
   * WHY WAITING EXISTS AT ALL. Firebase restores its session from the keychain
   * asynchronously at launch. Asking it for a token before that has finished
   * answers « no user is signed in », which is indistinguishable from a session
   * that is really gone. CAPACITOR-D is nine members who hit exactly that, and
   * CAPACITOR-V is the same moment answering with an empty token instead of an
   * error.
   *
   * Returns immediately when a user is already there, which is the ordinary
   * case and costs nothing. Returns null when the wait runs out, and that null
   * now means something: Firebase had its chance.
   */
  abstract waitForRestoredUser(timeoutMs: number): Promise<User | null>;
}

export const FirebaseFactory = (
  isBrowser: boolean,
  configuration: { firebaseConfig: FirebaseOptions },
): FirebaseService => {
  if (isBrowser) {
    return new DesktopFirebaseService(configuration.firebaseConfig);
  }
  return new MobileFirebaseService();
};

export class FirebaseConfig {}
